import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import BusinessQASection from './BusinessQASection';
import BusinessStatusBanner from './BusinessStatusBanner';
import BusinessStatusChips from './BusinessStatusChips';
import TrustChain from '../../Components/TrustChain/TrustChain';
import BusinessTrustChain from '../../Components/TrustChain/BusinessTrustChain';

import { Helmet } from 'react-helmet-async';
import EnhancedSEO from '../../Components/SEO/EnhancedSEO';
import {BusinessOwnerActions} from './BusinessOwnerActions';
import { BusinessLogo, UserLogo } from './Logos';
import { BusinessActions, BusinessInteractions } from './NonOwners';
import { BusinessRatings, NoReviewsYet, ReviewInteractionSection  } from './Reviews';
import ClaimBusiness from './ClaimBusiness';
import ReportBusiness from './ReportBusiness';
import BusinessFAQSection from './BusinessFAQSection';

import { 
  useBusinessInteractions, 
  useBusinessDialogs, 
  useBusinessNavigation 
} from './Hooks';

import moment from 'moment';

import {
  validateRating,
  validateReviewCount,
  hasValidSEOData,
  generateCanonicalUrl,
  generateBusinessSchema,
  generateFAQSchema,
  getValidatedReviewMicrodata,
  generatePageTitle,
  generatePageDescription,
  generatePageKeywords
} from './Seo/SeoUtils';

import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Paper,   
  Chip,
  Button,   
  Rating,
  Alert,
  useMediaQuery,
  useTheme,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  Collapse,
  Card,
  CardContent,
  SwipeableDrawer,
  Avatar,
  Divider
} from '@mui/material';

import { 
  Share as ShareIcon, 
  ArrowForward as ArrowForwardIcon,
  FiberNew as FiberNewIcon,
  Cake as CakeIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  AutoAwesome as ConceptIcon,
  Label as LabelIcon, 
  WorkspacePremium as PremiumIcon, 
  QrCode2 as QrCode2Icon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  Business as BusinessOwnerIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Star as StarIcon,
  Reviews as ReviewsIcon,
  Language as WebsiteIcon
} from '@mui/icons-material';

import { 
  BusinessOwnerSkeleton, 
  BusinessDetailsSkeleton, 
  BusinessStatusSkeleton 
} from './Skeletons';

import {useGetBusinessByIdQuery} from '../../redux/thunks/unauth/getbusinessbyid'
import { usePrefetch as useUserTrustChainPrefetch } from '../../redux/thunks/users/usertrustchainsget';
import { useGetUserEndorsementsQuery  } from '../../redux/thunks/users/userendorsements';
import { useGetUserFollowingQuery  } from '../../redux/thunks/users/userfollowings';
import { useGetUserSliceQuery } from '../../redux/thunks/users/getuserslice';
import { useGetBusinessOwnerViewQuery } from '../../redux/thunks/sp/getbusinessownerview';

import { 
  AIProcessingStatus, 
  AIAnalytics, 
  AICapabilitiesSummary 
} from './enhanced-ai-features';
import AIContentManager from './AIContentManager';
import AIToolsSection from './AIToolsSection';
import OwnerNextStepCard from './OwnerNextStepCard';
import AIQueryPreview from './AIQueryPreview';
import SkillStats from './SkillStats';
import { DesktopSponsoredAd, MobileSponsoredAd } from '../../Components/Ads';

// 🆕 Mobile Info Sidebar Component
const MobileInfoSidebar = ({ 
  isOpen, 
  onClose,
  profileData,
  ownerView,
  isBusinessOwner,
  account,
  businessInteractions,
  businessDialogs,
  businessNavigation,
  isTrustedThing,
  isFollowed,
  voiceLibrary,
  quotaUsage,
  getExistingVoiceData
}) => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    owner: true,
    interactions: true,
    stats: false,
    qr: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <SwipeableDrawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{
        sx: { 
          width: '100%',
          maxWidth: 400,
          bgcolor: 'background.default'
        }
      }}
    >
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Business Info
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2, overflow: 'auto', height: '100%' }}>
        {/* Business Owner Section */}
        <Card sx={{ mb: 2 }}>
          <CardContent 
            sx={{ 
              pb: 1, 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onClick={() => toggleSection('owner')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Business Owner</Typography>
            </Box>
            {expandedSections.owner ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          
          <Collapse in={expandedSections.owner}>
            <CardContent sx={{ pt: 0 }}>
              <Box 
                sx={{ 
                  textAlign: 'center', 
                  cursor: profileData.spEmail !== 'admin@revfer.com' ? 'pointer' : 'default',
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  mb: 2
                }}
                onClick={() => {
                  if (profileData.spEmail !== 'admin@revfer.com') {
                    navigate(`/profile/${profileData.spUserId}/active`);
                    onClose();
                  }
                }}
              >
                <UserLogo 
                  photoUrl={profileData.spPhoto} 
                  userName={profileData.spDisplayName || 'Revfer Systems'}
                />
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                  <Typography variant="h6">{profileData.spDisplayName}</Typography>
                  {!isBusinessOwner && profileData.spEmail !== 'admin@revfer.com' &&
                    <Box onClick={(e) => e.stopPropagation()}>
                      <TrustChain userId={profileData.spUserId} />
                    </Box>
                  }
                </Box>
                
                <Typography variant="subtitle2" color="text.secondary">
                  {profileData.spRole}
                </Typography>
              </Box>

              {/* Account Info */}
              <Box sx={{ mb: 2 }}>
                {isBusinessOwner ? (
                  account?.type === 'premium' ? (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        color: 'primary.main',
                        fontWeight: 'medium',
                        mb: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/premium');
                        onClose();
                      }}
                    >
                      <PremiumIcon fontSize="small" />
                      Premium until {account?.expiry ? moment(account.expiry).format('MMM-DD-YYYY') : 'N/A'}
                    </Typography>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PremiumIcon />}
                      onClick={() => {
                        navigate('/premium');
                        onClose();
                      }}
                      sx={{ mb: 1 }}
                    >
                      Upgrade to Premium
                    </Button>
                  )
                ) : (
                  profileData.accountType === 'premium' && (
                    <Chip
                      icon={<PremiumIcon />}
                      label="Premium Business"
                      color="primary"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  )
                )}
                
                {profileData.created && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FiberNewIcon fontSize="small" /> 
                    Joined {new Date(profileData.created).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </Typography>
                )}
              </Box>

              {/* AI Tools for Business Owner */}
              {isBusinessOwner && (
                <Box sx={{ mb: 2 }}>
                  <AIToolsSection
                    businessId={profileData._id}
                    businessName={profileData.businessName}
                    isBusinessOwner={isBusinessOwner}
                    accountType={account?.type || 'free'}
                    existingAudioDescription={ownerView?.audioDescription || null}
                    existingDocuments={profileData.documents || []}
                    voiceLibrary={voiceLibrary}
                    quotaUsage={quotaUsage}
                    googlePlaceId={ownerView?.googlePlaceId || null}
                    existingVoice={getExistingVoiceData()}
                  />
                </Box>
              )}

              {/* Business Owner Actions */}
              {isBusinessOwner && (
                <BusinessOwnerActions
                  profileData={profileData}
                  ownerView={ownerView}
                  onEditBusiness={businessNavigation.handlers.handleEditBusinessDetails}
                  onStartVerification={businessNavigation.handlers.handleStartVerification}
                  onUnclaim={businessNavigation.handlers.handleUnclaim}
                />
              )}
            </CardContent>
          </Collapse>
        </Card>

        {/* User Interactions Section */}
        {!isBusinessOwner && (
          <Card sx={{ mb: 2 }}>
            <CardContent 
              sx={{ 
                pb: 1, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={() => toggleSection('interactions')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BusinessOwnerIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Actions</Typography>
              </Box>
              {expandedSections.interactions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </CardContent>
            
            <Collapse in={expandedSections.interactions}>
              <CardContent sx={{ pt: 0 }}>
                <BusinessInteractions
                  onWriteReview={businessNavigation.handlers.handleWriteReview}
                  onEndorse={businessInteractions.handlers.handleEndorse}
                  onUnendorse={businessInteractions.handlers.handleUnEndorsement}
                  onFollow={businessInteractions.handlers.handleFollow}
                  onUnfollow={businessInteractions.handlers.handleUnFollow}
                  isTrustedThing={isTrustedThing}
                  isFollowed={isFollowed}
                  endorseButtonLoading={businessInteractions.loadingStates.endorseButtonLoading}
                  unendorseButtonLoading={businessInteractions.loadingStates.unendorseButtonLoading}
                  followButtonLoading={businessInteractions.loadingStates.followButtonLoading}
                  unfollowButtonLoading={businessInteractions.loadingStates.unfollowButtonLoading}
                  businessName={profileData.businessName}
                />
                
                <Divider sx={{ my: 2 }} />
                
                <BusinessActions
                  onClaimBusiness={businessDialogs.handlers.handleOpenClaimDialog}
                  onReportBusiness={businessDialogs.handlers.handleOpenReportDialog}
                  onShareBusiness={businessDialogs.handlers.handleShareBusiness}
                  businessId={profileData._id}
                  businessName={profileData.businessName}
                />
              </CardContent>
            </Collapse>
          </Card>
        )}

        {/* Stats Section */}
        <Card sx={{ mb: 2 }}>
          <CardContent 
            sx={{ 
              pb: 1, 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onClick={() => toggleSection('stats')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StarIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Business Stats</Typography>
            </Box>
            {expandedSections.stats ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          
          <Collapse in={expandedSections.stats}>
            <CardContent sx={{ pt: 0 }}>
              <SkillStats skill={profileData} spId={profileData.spId} />
            </CardContent>
          </Collapse>
        </Card>

        {/* QR Code Section */}
        {profileData.qrCodeUrl && (
          <Card sx={{ mb: 2 }}>
            <CardContent 
              sx={{ 
                pb: 1, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={() => toggleSection('qr')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <QrCode2Icon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">QR Code</Typography>
              </Box>
              {expandedSections.qr ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </CardContent>
            
            <Collapse in={expandedSections.qr}>
              <CardContent sx={{ pt: 0, textAlign: 'center' }}>
                <Box
                  component="img"
                  src={profileData.qrCodeUrl}
                  alt={`${profileData.businessName} QR Code`}
                  sx={{
                    width: '100%',
                    maxWidth: '200px',
                    height: 'auto',
                    borderRadius: 1,
                    mb: 2,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                />
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Scan to share this business
                </Typography>
                
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ShareIcon />}
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = profileData.qrCodeUrl;
                    link.download = `${profileData.businessName}-QRCode.png`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                >
                  Download QR Code
                </Button>
              </CardContent>
            </Collapse>
          </Card>
        )}

        {/* Advertisement Space */}
        {/* Google AdSense - Mobile */}
        <MobileSponsoredAd />
      </Box>
    </SwipeableDrawer>
  );
};

// 🆕 Mobile Review Item Component
const MobileReviewItem = ({ review, navigate, businessName }) => {
  const reviewMicrodata = getValidatedReviewMicrodata(review);
  
  return (
    <article itemScope itemType="https://schema.org/Review">
      <Paper variant="outlined" sx={{ p: 2, mb: 2, border: 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <Typography 
              variant="subtitle1" 
              component="button"
              onClick={() => navigate(`/profile/${review.userId}/active`)}
              sx={{ 
                cursor: 'pointer', 
                textDecoration: 'none',
                color: 'inherit',
                background: 'none',
                border: 'none',
                padding: 0,
                font: 'inherit',
                textAlign: 'left',
                '&:hover': {
                  color: 'primary.main',
                  textDecoration: 'underline'
                }
              }}
              itemProp="author"
              itemScope
              itemType="https://schema.org/Person"
            >
              <span itemProp="name">{review.userDisplayName}</span>
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              itemProp="datePublished"
              dateTime={review.created}
            >
              {new Date(review.created).toLocaleDateString()}
            </Typography>
          </Box>
          
          <Box 
            display="flex" 
            alignItems="center"
            sx={{ flexShrink: 0 }}
            {...(reviewMicrodata.showMicrodata && {
              itemProp: "reviewRating",
              itemScope: true,
              itemType: "https://schema.org/Rating"
            })}
          >
            {reviewMicrodata.showMicrodata && (
              <>
                <meta itemProp="ratingValue" content={reviewMicrodata.validRating.toString()} />
                <meta itemProp="bestRating" content="5" />
                <meta itemProp="worstRating" content="1" />
              </>
            )}
            
            <Rating 
              value={Math.round(reviewMicrodata.displayRating * 10) / 10} 
              precision={0.1} 
              readOnly 
              size="small" 
            />
            <Typography variant="body2" sx={{ ml: 1 }}>
              {Math.round(reviewMicrodata.displayRating * 10) / 10}
            </Typography>
          </Box>
        </Box>
        
        <Typography variant="body2" sx={{ mb: 1 }} itemProp="reviewBody">
          {review.review}
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {review.likeCount} Likes
          </Typography>
          {review.verified && (
            <Chip 
              label="Verified" 
              color="success" 
              size="small" 
              variant="outlined" 
            />
          )}
        </Box>
        
        <ReviewInteractionSection 
          review={review}
          businessName={businessName}
        />
      </Paper>
    </article>
  );
};

// 🆕 Enhanced Main Component with Mobile Support
const BusinessDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Mobile-specific state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const { authStatus } = useAuthenticator(context => [context.authStatus]);
  const { businessId, title } = useParams();
  const token = useSelector((state) => state.auth?.token || null);
  const spId = useSelector((state) => state.auth?.user?.spId || null); 
  const account = useSelector((state) => state.auth?.user?.accountDetails || null);

  const isAuthenticated = authStatus === 'authenticated' && token;

  // === Voice-related state and queries ===
  const [voiceLibrary, setVoiceLibrary] = useState(null);
  const [quotaUsage, setQuotaUsage] = useState(null);

  // === Core component state ===
  const [isLoading, setIsLoading] = useState(true);
  const [isBusinessOwner, setIsBusinessOwner] = useState(false);
  const [profileData, setProfileData] = useState('');
  const [thisToken, setThisToken] = useState(null);
  const [thisSpId, setThisSpId] = useState(null);
  const [isTrustedThing, setIsTrustedThing] = useState(false)
  const [isFollowed, setIsFollowed] = useState(false)
  const [businessDisplayStatus, setBusinessDisplayStatus] = useState('active');
  const [tempMessage, setTempMessage] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [sortCriteria, setSortCriteria] = useState('date');

  const show = true;

  const [userInteractions, setUserInteractions] = useState({
      isFollowing: false,
      hasEndorsed: false,
      reviewCount: 0
  });

  const prefetchUserTrustChain = useUserTrustChainPrefetch('getUserTrustchain');

  // === API Queries ===
  const args = {
    businessId: businessId,
    authType: 'none',
    token: token,
    title: title
  }

  const { data: profile, error: profileError, isLoading: profileLoading } = useGetBusinessByIdQuery(args);
  const { data: myTrustedThings, error: myTrustedThingsError, isLoading: myTrustedThingsLoading } = useGetUserEndorsementsQuery();
  const { data: followings, error: followingsError, isLoading: followingsLoading } = useGetUserFollowingQuery();
  const { data: userVoices, isError: isuserVoicesError, isLoading: isuserVoicesLoading, error: userVoicesError } = useGetUserSliceQuery('voiceLibrary, quotaUsage');

   // Owner-only fields. A 403 is the NORMAL answer for any signed-in visitor
  // looking at someone else's business, so this is not an error path — it is
  // how the server says "not the owner". Skipped entirely when signed out.
  const { data: ownerView, isLoading: ownerViewLoading } = useGetBusinessOwnerViewQuery(businessId, {
    skip: !isAuthenticated || !businessId,
  });
  // === Custom Hooks for Handler Management ===
  const businessInteractions = useBusinessInteractions({
    token: thisToken,
    businessId,
    title,
    profileData,
    isTrustedThing,
    setIsTrustedThing,
    isFollowed,
    setIsFollowed
  });

  const businessDialogs = useBusinessDialogs({
    token: thisToken,
    businessId,
    title
  });

  const businessNavigation = useBusinessNavigation({
    token: thisToken,
    businessId,
    title,
    profileData,
    ownerView
  });
  console.log('profile', profile)

  // === Voice data processing ===
  useEffect(() => {
    if (userVoices && isBusinessOwner) {
      const businessVoices = userVoices.voiceLibrary?.business?.[businessId] || [];
      setVoiceLibrary({
        voices: businessVoices,
        totalVoices: businessVoices.length,
        lastUpdated: userVoices.voiceLibrary?.lastUpdated || null
      });

      const voiceQuotaData = userVoices.quotaUsage?.voice_cloning || {};
      const ttsQuotaData = userVoices.quotaUsage?.voice_tts_generation || {};
      setQuotaUsage({
        voice_cloning: voiceQuotaData,
        ai_image_generation: userVoices.quotaUsage?.ai_image_generation || {},
        social_media_content: userVoices.quotaUsage?.social_media_content || {},
        voice_tts_generation: ttsQuotaData || {}
      });
    } else if (!isBusinessOwner) {
      setVoiceLibrary(null);
      setQuotaUsage(null);
    }
  }, [userVoices, isBusinessOwner, businessId]);

  const getExistingVoiceData = () => {
    if (!voiceLibrary || !voiceLibrary.voices || voiceLibrary.voices.length === 0) {
      return null;
    }

    const activeVoices = voiceLibrary.voices.filter(voice => voice.status === 'active');
    if (activeVoices.length === 0) {
      return voiceLibrary.voices[0];
    }

    return activeVoices.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
  };

  // === Authentication state management ===
  useEffect(() => {
    if (authStatus === 'authenticated') {
      setThisToken(token);
      setThisSpId(spId);
    }
  }, [authStatus, token, spId]);

  // === Trusted things management ===
  useEffect(() => {
    if (myTrustedThings && profile) {
      const isTrustedThing = myTrustedThings.myTrustedThings.some((followingItem) => {
        return (followingItem === profile._id);
      });
      setIsTrustedThing(isTrustedThing);         
    }
  }, [myTrustedThings, profile]);

  // === Following state management ===
  useEffect(() => {
    if (followings && profile) {
      const isUserFollowed = followings.following.some((followingItem) => {
        return (followingItem === profile._id);
      });
      setIsFollowed(isUserFollowed);
    }
  }, [followings, profile]);

  // === Loading state management ===
  useEffect(() => {
    setIsLoading(profileLoading || myTrustedThingsLoading || followingsLoading || ownerViewLoading);
  }, [profileLoading, myTrustedThingsLoading, followingsLoading, ownerViewLoading]);

   // === Profile data processing ===
  useEffect(() => {
    if (profile?.businessDisplayStatus) {
      setBusinessDisplayStatus(profile.businessDisplayStatus);
    }
    if (profile) setProfileData(profile);
  }, [profile]);

  // Ownership is now the SERVER's answer, not a client-side comparison.
  //
  // It used to be `thisSpId !== profile.spId`, decided from data every visitor
  // already had — so the server never learned who was asking, and every
  // owner-only field had to be public for the page to work. ownerView returns
  // 200 only when the Cognito identity matches spUserId; a 403 means not the
  // owner, and RTK surfaces that as absent data rather than an error.
  useEffect(() => {
    setIsBusinessOwner(!!ownerView?.isOwner);
  }, [ownerView]);

  // Prefetch the owner's trust chain for visitors, as before.
  useEffect(() => {
    if (profile?.spUserId && !ownerView?.isOwner && profile.spEmail !== 'admin@revfer.com') {
      prefetchUserTrustChain(profile.spUserId);
    }
  }, [profile, ownerView]);

  // === Message handling ===
  useEffect(() => {
    if (location.state?.reviewSubmitted) {
      setTempMessage({
        type: 'success',
        title: 'Review Submitted Successfully!',
        description: `Thank you for sharing your experience with ${profileData.businessName}. Your review will help others in the community.`
      });
      
      const timer = setTimeout(() => {
        setTempMessage(null);
      }, 5000);
      
      window.history.replaceState({}, document.title);
      return () => clearTimeout(timer);
    }
    
    if (location.state?.message) {
      setTempMessage(location.state.message);
      
      const timer = setTimeout(() => {
        setTempMessage(null);
      }, 5000);
      
      window.history.replaceState({}, document.title);
      return () => clearTimeout(timer);
    }
  }, [location, profileData]);

  // === Utility functions ===
  const sortReviews = (criteria) => {
    const sortedReviews = [...profileData.reviews].sort((a, b) => {
      switch(criteria) {
        case 'rating':
          return b.rating - a.rating;
        case 'smartRating':
          return b.SkillValue_all - a.SkillValue_all;
        case 'date':
        default:
          return new Date(b.date) - new Date(a.date);
      }
    });
    setReviews(sortedReviews);
    setSortCriteria(criteria);
  };

  // === Error handling ===
  if (profileError) {
    return (
      <>
        <EnhancedSEO 
          title="Business Not Found - Revfer"
          description="The business you're looking for could not be found. Discover other great local businesses on Revfer."
          keywords="business not found, local businesses, business directory"
          type="website"
          image="/og-image.jpg"
        />
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Alert severity="error">
            Business not found. Please check the URL or try searching for other businesses.
          </Alert>
        </Container>
      </>
    );
  }

  // === Loading state ===
  if (isLoading) {
    return (
      <>
        <EnhancedSEO 
          title="Loading Business - Revfer"
          description="Loading business information. Discover trusted local businesses with authentic reviews on Revfer."
          keywords="local business, business directory, reviews"
          type="website"
          image="/og-image.jpg"
        />
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <BusinessOwnerSkeleton />
            </Grid>
            <Grid item xs={12} md={6}>
              <BusinessDetailsSkeleton />
            </Grid>
            <Grid item xs={12} md={3}>
              <BusinessStatusSkeleton />
            </Grid>
          </Grid>
        </Container>
      </>
    );
  }

  // === SEO Data Generation ===
  const canonicalUrl = generateCanonicalUrl(profileData);
  const businessSchema = generateBusinessSchema(profileData);
  const faqSchema = generateFAQSchema(profileData);
  const pageTitle = generatePageTitle(profileData);
  const pageDescription = generatePageDescription(profileData);
  const pageKeywords = generatePageKeywords(profileData);

  return (
    <>
      {/* Enhanced SEO Implementation */}
      <EnhancedSEO 
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        type="website"
        image={profileData.photoUrl || "/og-image.jpg"}
        author={profileData.spDisplayName}
        dateModified={profileData.updated || profileData.created}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Businesses', url: '/businesses' },
          { name: profileData.businessName, url: `/business/${profileData._id}/${title}` }
        ]}
      />

      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
        
        {businessSchema && (
          <script type="application/ld+json">
            {JSON.stringify(businessSchema)}
          </script>
        )}
        
        {faqSchema && (
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        )}

        <meta name="business-name" content={profileData.businessName} />
        <meta name="business-category" content={Array.isArray(profileData.category) ? profileData.category.join(', ') : profileData.category} />
        <meta name="business-location" content={profileData.geocodeResults?.formattedAddress || 'Local Business'} />
        <meta name="business-rating" content={profileData.avgSkillRating_all || '0'} />
        <meta name="business-reviews" content={profileData.reviews?.length || '0'} />
        <meta name="business-owner" content={profileData.spDisplayName} />
      </Helmet>

      <main itemScope itemType="https://schema.org/LocalBusiness">
        <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 3, px: { xs: 2, sm: 3 } }}>
          {!isAuthenticated && (
            <Alert 
              severity="info" 
              sx={{ mb: 2 }}
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={() => navigate('/auth/signin', { 
                    state: { 
                      name: 'businessDetails', 
                      link: `/business/${businessId}/${title}` 
                    } 
                  })}
                >
                  Sign In
                </Button>
              }
            >
              Sign in to interact with this business, write reviews, and more.
            </Alert>
          )}

          {isMobile ? (
            // 🆕 Mobile Layout - Single Column
            <>
              {/* Mobile Header with Menu */}
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  <BusinessLogo 
                    photoUrl={profileData.photoUrl} 
                    businessName={profileData.businessName}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                      <Typography variant="h5" component="h1" itemProp="name" noWrap>
                        {profileData.businessName}
                      </Typography>
                      <Box sx={{ transform: 'translateY(-4px)' }}>
                        <BusinessTrustChain 
                          businessId={profileData._id}
                          businessName={profileData.businessName}
                          isBusinessOwner={isBusinessOwner}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {Array.isArray(profileData.category)
                        ? profileData.category[0]
                        : profileData.category}
                    </Typography>
                  </Box>
                </Box>
                
                <IconButton 
                  color="primary" 
                  onClick={() => setSidebarOpen(true)}
                  sx={{ ml: 1 }}
                >
                  <MenuIcon />
                </IconButton>
              </Paper>

              {isBusinessOwner && (
                <OwnerNextStepCard
                  businessId={profileData._id}
                  businessName={profileData.businessName}
                  businessQuality={ownerView?.businessQuality}
                />
              )}

              {/* Status Banner */}
              <BusinessStatusBanner 
                tags={profileData.tags} 
                tempMessage={tempMessage}
                profileData={profileData}
                isBusinessOwner={isBusinessOwner}
                businessQuality={ownerView?.businessQuality}
                onEditBusiness={businessNavigation.handlers.handleEditBusinessDetails}
              />

              {/* Main Business Info */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, border: 'none' }}>
                <article itemScope itemType="https://schema.org/LocalBusiness">
                  {profileData.description && (
                    <Typography variant="body2" component="p" itemProp="description" sx={{ mb: 2 }}>
                      {profileData.description}
                    </Typography>
                  )}

                  <BusinessRatings
                    profileData={profileData}
                    hasValidSEOData={hasValidSEOData(profileData)}
                    validateRating={validateRating}
                  />

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 2 }}>
                    <BusinessStatusChips tags={profileData.tags} maxChips={2} />
                  </Box>

                  {/* Business Information */}
                  <Box sx={{ mt: 2 }}>
                    {profileData.geocodeResults?.formattedAddress && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <LocationIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          itemProp="address"
                          sx={{ wordBreak: 'break-word' }}
                        >
                          {profileData.geocodeResults.formattedAddress}
                        </Typography>
                      </Box>
                    )}

                    {profileData.businessHours && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TimeIcon fontSize="small" color="action" />
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          itemProp="openingHours"
                        >
                          {profileData.businessHours}
                        </Typography>
                      </Box>
                    )}

                    {profileData.businessConcept && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <ConceptIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          itemProp="description"
                        >
                          {profileData.businessConcept}
                        </Typography>
                      </Box>
                    )}
                    {profileData.businessWebsite && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <WebsiteIcon fontSize="small" color="action" />
                        <Typography 
                          variant="body2" 
                          component="a"
                          href={profileData.businessWebsite.startsWith('http') ? profileData.businessWebsite : `https://${profileData.businessWebsite}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          itemProp="url"
                          sx={{ 
                            color: 'primary.main',
                            textDecoration: 'none',
                            wordBreak: 'break-all',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {profileData.businessWebsite.replace(/^https?:\/\//, '')}
                        </Typography>
                      </Box>
                    )}

                    {profileData.featureMap && Array.isArray(profileData.featureMap) && profileData.featureMap.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography 
                          variant="subtitle2" 
                          color="text.primary" 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            mb: 1,
                            fontWeight: 600
                          }}
                        >
                          <LabelIcon fontSize="small" color="primary" />
                          Features & Amenities
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {profileData.featureMap.slice(0, 6).map((feature, index) => (
                            <Chip
                              key={index}
                              label={feature}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ borderRadius: '16px' }}
                            />
                          ))}
                          {profileData.featureMap.length > 6 && (
                            <Chip
                              label={`+${profileData.featureMap.length - 6} more`}
                              size="small"
                              variant="outlined"
                              color="default"
                              sx={{ borderRadius: '16px' }}
                            />
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </article>
              </Paper>

              {/* Q&A Section */}
              <BusinessQASection 
                businessName={profileData.businessName}
                businessId={profileData._id}
                hasDocuments={profileData.documents?.length > 0}
                hasAudio={!!profileData.hasAudioDescription}
                documentTypes={profileData.documents?.map(doc => doc.type) || []}
                isPreview={false}
                isBusinessOwner={isBusinessOwner}
              />

              {/* FAQ Section */}
              <BusinessFAQSection
                profileData={profileData}
                isBusinessOwner={isBusinessOwner}
              />

              {/* Reviews Section - Mobile Optimized */}
              <section aria-label="Customer reviews">
                <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
                  {profileData.reviews && profileData.reviews.length > 0 ? (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                          Customer Reviews
                        </Typography>
                        
                        {/* Mobile-friendly sort buttons */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button 
                            variant={sortCriteria === 'date' ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => sortReviews('date')}
                          >
                            Recent
                          </Button>
                          <Button 
                            variant={sortCriteria === 'rating' ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => sortReviews('rating')}
                          >
                            Top Rated
                          </Button>
                          <Button 
                            variant={sortCriteria === 'smartRating' ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => sortReviews('smartRating')}
                          >
                            Smart Rating
                          </Button>
                        </Box>
                      </Box>

                      {/* Show first 3 reviews on mobile */}
                      {profileData.reviews.slice(0, 3).map((review) => (
                        <MobileReviewItem
                          key={review.id}
                          review={review}
                          navigate={navigate}
                          businessName={title}
                        />
                      ))}

                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => navigate(`/business/${profileData._id}/reviews`)}
                          endIcon={<ArrowForwardIcon />}
                          fullWidth
                        >
                          View All Reviews ({profileData.reviews.length})
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <NoReviewsYet 
                      businessName={profileData.businessName}
                      onWriteReview={businessNavigation.handlers.handleWriteReview}
                      isAuthenticated={isAuthenticated}
                    />
                  )}
                </Paper>
              </section>

              {/* Mobile Info Sidebar */}
              <MobileInfoSidebar
                isOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                profileData={profileData}
                ownerView={ownerView}
                isBusinessOwner={isBusinessOwner}
                account={account}
                businessInteractions={businessInteractions}
                businessDialogs={businessDialogs}
                businessNavigation={businessNavigation}
                isTrustedThing={isTrustedThing}
                isFollowed={isFollowed}
                voiceLibrary={voiceLibrary}
                quotaUsage={quotaUsage}
                getExistingVoiceData={getExistingVoiceData}
              />

              {/* Dialogs */}
              <ClaimBusiness
                isOpen={businessDialogs.dialogStates.isClaimDialogOpen}
                onClose={businessDialogs.handlers.handleCloseClaimDialog}
                businessId={profileData._id}
              />
              
              <ReportBusiness 
                isOpen={businessDialogs.dialogStates.isReportDialogOpen} 
                onClose={businessDialogs.handlers.handleCloseReportDialog} 
                businessId={profileData._id} 
              />
            </>
          ) : (
            // 🆕 Desktop Layout - Original 3 Columns (unchanged)
            <Grid container spacing={3}>
              {/* Left Column - Business Owner */}
              <Grid item xs={12} md={3}>
                <section aria-label="Business owner information"> 
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, textAlign: 'center', mb: 2, border: 'none', cursor: profileData.spEmail !== 'admin@revfer.com' ? 'pointer' : 'default' }}
                    onClick={() => {
                      if (profileData.spEmail !== 'admin@revfer.com') {
                        navigate(`/profile/${profileData.spUserId}/active`);
                      }
                    }}
                  >
                    <UserLogo 
                      photoUrl={profileData.spPhoto} 
                      userName={profileData.spDisplayName || 'Revfer Systems'}
                    />
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="h6" component="h2" itemProp="name">{profileData.spDisplayName} </Typography>
                      {!isBusinessOwner && profileData.spEmail !== 'admin@revfer.com' &&
                        <Box onClick={(e) => e.stopPropagation()}>
                          <TrustChain userId={profileData.spUserId} />
                        </Box>
                      }
                    </Box>
                    
                    <Typography variant="subtitle2" color="text.secondary">
                      {profileData.spRole}
                    </Typography>
                    
                    <Box sx={{ mt: 2, textAlign: 'left', px: 2 }}>
                      {isBusinessOwner ? (
                        account?.type === 'premium' ? (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              color: 'primary.main',
                              fontWeight: 'medium',
                              mb: 1,
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/premium');
                            }}
                          >
                            <PremiumIcon fontSize="small" sx={{ color: 'primary.main' }} />
                            Premium Business Account until {account?.expiry ? moment(account.expiry).format('MMM-DD-YYYY') : 'N/A'}
                          </Typography>
                        ) : (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              color: 'text.secondary',
                              mb: 1,
                              cursor: 'pointer',
                              '&:hover': {
                                color: 'primary.main',
                                textDecoration: 'underline'
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/premium');
                            }}
                          >
                            <PremiumIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            Upgrade to Premium Business
                          </Typography>
                        )
                      ) : (
                        profileData.accountType === 'premium' ? (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              color: 'primary.main',
                              fontWeight: 'medium',
                              mb: 1
                            }}
                          >
                            <PremiumIcon fontSize="small" sx={{ color: 'primary.main' }} />
                            Premium Business
                          </Typography>
                        ) : null
                      )}
                      
                      {profileData.created && (
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FiberNewIcon fontSize="small" sx={{ color: 'info.light' }} /> 
                          Joined {new Date(profileData.created).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </Typography>
                      )}
                      
                      {profileData.spTag && (
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CakeIcon fontSize="small" sx={{ color: 'info.light' }} /> 
                          {profileData.spTag}
                        </Typography>
                      )}
                    </Box>
                  </Paper>

                   {isBusinessOwner && (
                    <AIToolsSection
                      businessId={profileData._id}
                      businessName={profileData.businessName}
                      isBusinessOwner={isBusinessOwner}
                      accountType={account?.type || 'free'}
                      existingAudioDescription={ownerView?.audioDescription || null}
                      existingDocuments={profileData.documents || []}
                      voiceLibrary={voiceLibrary}
                      quotaUsage={quotaUsage}
                      googlePlaceId={ownerView?.googlePlaceId || null}
                      existingVoice={getExistingVoiceData()}
                    />
                  )}
                  {!isBusinessOwner && (
                    <BusinessInteractions
                      onWriteReview={businessNavigation.handlers.handleWriteReview}
                      onEndorse={businessInteractions.handlers.handleEndorse}
                      onUnendorse={businessInteractions.handlers.handleUnEndorsement}
                      onFollow={businessInteractions.handlers.handleFollow}
                      onUnfollow={businessInteractions.handlers.handleUnFollow}
                      isTrustedThing={isTrustedThing}
                      isFollowed={isFollowed}
                      endorseButtonLoading={businessInteractions.loadingStates.endorseButtonLoading}
                      unendorseButtonLoading={businessInteractions.loadingStates.unendorseButtonLoading}
                      followButtonLoading={businessInteractions.loadingStates.followButtonLoading}
                      unfollowButtonLoading={businessInteractions.loadingStates.unfollowButtonLoading}
                      businessName={profileData.businessName}
                    />
                  )}

                  <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
                      {isBusinessOwner && (
                       <BusinessOwnerActions
                         profileData={profileData}
                         ownerView={ownerView}
                         onEditBusiness={businessNavigation.handlers.handleEditBusinessDetails}
                         onStartVerification={businessNavigation.handlers.handleStartVerification}
                         onUnclaim={businessNavigation.handlers.handleUnclaim}
                       />
                      )}

                    {!isBusinessOwner && (
                      <>
                        <BusinessActions
                          onClaimBusiness={businessDialogs.handlers.handleOpenClaimDialog}
                          onReportBusiness={businessDialogs.handlers.handleOpenReportDialog}
                          onShareBusiness={businessDialogs.handlers.handleShareBusiness}
                          businessId={profileData._id}
                          businessName={profileData.businessName}
                        />
                        
                        <ClaimBusiness
                          isOpen={businessDialogs.dialogStates.isClaimDialogOpen}
                          onClose={businessDialogs.handlers.handleCloseClaimDialog}
                          businessId={profileData._id}
                        />
                        
                        <ReportBusiness 
                          isOpen={businessDialogs.dialogStates.isReportDialogOpen} 
                          onClose={businessDialogs.handlers.handleCloseReportDialog} 
                          businessId={profileData._id} 
                        />
                      </>
                    )}
                  </Paper>
                </section>
              </Grid>

              {/* Middle Column - Business Details */}
              <Grid item xs={12} md={6}>
                {isBusinessOwner && (
                  <OwnerNextStepCard
                    businessId={profileData._id}
                    businessName={profileData.businessName}
                    businessQuality={ownerView?.businessQuality}
                  />
                )}
                <article itemScope itemType="https://schema.org/LocalBusiness">
                <BusinessStatusBanner 
                tags={profileData.tags} 
                tempMessage={tempMessage}
                profileData={profileData}
                isBusinessOwner={isBusinessOwner}
                businessQuality={ownerView?.businessQuality}
                onEditBusiness={businessNavigation.handlers.handleEditBusinessDetails}
              />

                  <Paper variant="outlined" sx={{ p: 2, mb: 2, border: 'none' }}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        mb: 2 
                      }}
                    >
                      <BusinessLogo 
                        photoUrl={profileData.photoUrl} 
                        businessName={profileData.businessName}
                      />
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                          <Typography variant="h4" component="h1" itemProp="name">
                            {profileData.businessName}
                          </Typography>
                          <Box sx={{ transform: 'translateY(-8px)' }}>
                            <BusinessTrustChain 
                              businessId={profileData._id}
                              businessName={profileData.businessName}
                              isBusinessOwner={isBusinessOwner}
                            />
                          </Box>
                        </Box>
                        
                        {profileData.description && (
                          <Typography variant="body2" component="p" itemProp="description" sx={{ mt: 1, mb: 1 }}>
                            {profileData.description}
                          </Typography>
                        )}
                        
                        <Typography variant="subtitle1" color="text.secondary">
                          {Array.isArray(profileData.category)
                            ? profileData.category.slice(0, 3).map((cat, index) => (
                                <React.Fragment key={index}>
                                  {cat}
                                  {index < 2 && (
                                    <span style={{ fontSize: '1.2em', fontWeight: 'bold' }}> · </span>
                                  )}
                                </React.Fragment>
                              ))
                            : profileData.category}
                        </Typography>
                      </Box>
                    </Box>

                    <BusinessRatings
                      profileData={profileData}
                      hasValidSEOData={hasValidSEOData(profileData)}
                      validateRating={validateRating}
                    />

                    <Box sx={{ display: 'flex', gap: 1, my: 2 }}>
                      <BusinessStatusChips tags={profileData.tags} maxChips={3} />
                    </Box>

                    {/* Business Information Section */}
                    <Box sx={{ mt: 2 }}>
                      {(profileData.geocodeResults?.formattedAddress || profileData.businessHours) && (
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: { xs: 1, sm: 3 },
                          mb: 1,
                          alignItems: { xs: 'flex-start', sm: 'center' }
                        }}>
                          {profileData.geocodeResults?.formattedAddress && (
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              flex: { xs: 'none', sm: '1' }
                            }}>
                              <LocationIcon fontSize="small" color="action" />
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                itemProp="address"
                                sx={{ 
                                  wordBreak: 'break-word',
                                  lineHeight: 1.4
                                }}
                              >
                                {profileData.geocodeResults.formattedAddress}
                              </Typography>
                            </Box>
                          )}

                          {profileData.businessHours && (
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              flex: 'none',
                              minWidth: 'fit-content'
                            }}>
                              <TimeIcon fontSize="small" color="action" />
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                itemProp="openingHours"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {profileData.businessHours}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}

                      {profileData.businessConcept && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                          itemProp="description"
                        >
                          <ConceptIcon fontSize="small" color="action" />
                          {profileData.businessConcept}
                        </Typography>
                      )}
                      {profileData.businessWebsite && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <WebsiteIcon fontSize="small" color="action" />
                          <Typography 
                            variant="body2" 
                            component="a"
                            href={profileData.businessWebsite.startsWith('http') ? profileData.businessWebsite : `https://${profileData.businessWebsite}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            itemProp="url"
                            sx={{ 
                              color: 'primary.main',
                              textDecoration: 'none',
                              wordBreak: 'break-all',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            {profileData.businessWebsite.replace(/^https?:\/\//, '')}
                          </Typography>
                        </Box>
                      )}

                      {profileData.featureMap && Array.isArray(profileData.featureMap) && profileData.featureMap.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography 
                            variant="subtitle2" 
                            color="text.primary" 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1, 
                              mb: 1,
                              fontWeight: 600
                            }}
                          >
                            <LabelIcon fontSize="small" color="primary" />
                            Features & Amenities
                          </Typography>
                          <Box sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 1,
                            mt: 1 
                          }}>
                            {profileData.featureMap.map((feature, index) => (
                              <Chip
                                key={index}
                                label={feature}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{
                                  borderRadius: '16px',
                                  '&:hover': {
                                    backgroundColor: 'primary.light',
                                    color: 'primary.contrastText'
                                  }
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                
                  <BusinessQASection 
                    businessName={profileData.businessName}
                    businessId={profileData._id}
                    hasDocuments={profileData.documents?.length > 0}
                    hasAudio={!!profileData.hasAudioDescription}
                    documentTypes={profileData.documents?.map(doc => doc.type) || []}
                    isPreview={false}
                    isBusinessOwner={isBusinessOwner}
                  />
                  {/* FAQ Section */}
                    <BusinessFAQSection
                      profileData={profileData}
                      isBusinessOwner={isBusinessOwner}
                    />

                  {/* Reviews Section */}
                  <section aria-label="Customer reviews">
                    <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
                      {profileData.reviews && profileData.reviews.length > 0 ? (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" component="h2">Customer Reviews</Typography>
                            <Box>
                              <Button 
                                variant={sortCriteria === 'date' ? 'contained' : 'outlined'}
                                size="small"
                                sx={{ mr: 1 }}
                                onClick={() => sortReviews('date')}
                              >
                                Recent
                              </Button>
                              <Button 
                                variant={sortCriteria === 'rating' ? 'contained' : 'outlined'}
                                size="small"
                                sx={{ mr: 1 }}
                                onClick={() => sortReviews('rating')}
                              >
                                Highest Rated
                              </Button>
                              <Button 
                                variant={sortCriteria === 'smartRating' ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => sortReviews('smartRating')}
                              >
                                Smart Rating
                              </Button>
                            </Box>
                          </Box>

                          {profileData.reviews.map((review) => {
                            const reviewMicrodata = getValidatedReviewMicrodata(review);
                            
                            return (
                              <article 
                                key={review.id} 
                                itemScope 
                                itemType="https://schema.org/Review"
                              > 
                                <Paper 
                                  key={review.id} 
                                  variant="outlined" 
                                  sx={{ p: 2, mb: 2, border: 'none' }}
                                >
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                      <Typography 
                                        variant="subtitle1" 
                                        component="a" 
                                        onClick={() => navigate(`/profile/${review.userId}/active`)}
                                        sx={{ 
                                          cursor: 'pointer', 
                                          textDecoration: 'none',
                                          color: 'inherit',
                                          '&:hover': {
                                            color: 'primary.main',
                                            textDecoration: 'underline'
                                          }
                                        }}
                                        itemProp="author"
                                        itemScope
                                        itemType="https://schema.org/Person"
                                      >
                                        <span itemProp="name">{review.userDisplayName}</span>
                                      </Typography>
                                      <Typography 
                                        variant="body2" 
                                        color="text.secondary"
                                        itemProp="datePublished"
                                        dateTime={review.created}
                                      >
                                        {new Date(review.created).toLocaleDateString()}
                                      </Typography>
                                    </Box>
                                    
                                    <Box 
                                      display="flex" 
                                      alignItems="center"
                                      {...(reviewMicrodata.showMicrodata && {
                                        itemProp: "reviewRating",
                                        itemScope: true,
                                        itemType: "https://schema.org/Rating"
                                      })}
                                    >
                                      {reviewMicrodata.showMicrodata && (
                                        <>
                                          <meta itemProp="ratingValue" content={reviewMicrodata.validRating.toString()} />
                                          <meta itemProp="bestRating" content="5" />
                                          <meta itemProp="worstRating" content="1" />
                                        </>
                                      )}
                                      
                                      <Rating 
                                        value={Math.round(reviewMicrodata.displayRating * 10) / 10} 
                                        precision={0.1} 
                                        readOnly 
                                        size="small" 
                                      />
                                      <Typography variant="body2" sx={{ ml: 1 }}>
                                        {Math.round(reviewMicrodata.displayRating * 10) / 10}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  
                                  <Typography variant="body2" sx={{ mt: 1 }} itemProp="reviewBody">
                                    {review.review}
                                  </Typography>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {review.likeCount} Likes
                                    </Typography>
                                    {review.verified && (
                                      <Chip 
                                        label="Verified" 
                                        color="success" 
                                        size="small" 
                                        variant="outlined" 
                                      />
                                    )}
                                  </Box>
                                  <ReviewInteractionSection 
                                    review={review}
                                    businessName={title}
                                  />
                                </Paper>
                              </article>
                            );
                          })}

                          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={() => navigate(`/business/${profileData._id}/reviews`)}
                              endIcon={<ArrowForwardIcon />}
                            >
                              Show All Reviews
                            </Button>
                          </Box>
                        </>
                      ) : (
                        <NoReviewsYet 
                          businessName={profileData.businessName}
                          onWriteReview={businessNavigation.handlers.handleWriteReview}
                          isAuthenticated={isAuthenticated}
                        />
                      )}
                    </Paper>
                  </section>
                </article>
              </Grid>

              {/* Right Column - Business Status */}
              <Grid item xs={12} md={3}>
              <aside aria-label="Additional business information">
                {/* Google AdSense - Desktop Sidebar */}
                <DesktopSponsoredAd />

                <Paper variant="outlined" sx={{ p: 2, border: 'none', mb: 1, borderRadius: 2 }}>
                  <SkillStats skill={profileData} spId={profileData.spId} />
                </Paper>

                {profileData.qrCodeUrl && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, border: 'none' }}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <QrCode2Icon color="primary" fontSize="small" />
                        Business QR Code
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box
                          component="img"
                          src={profileData.qrCodeUrl}
                          alt={`${profileData.businessName} QR Code`}
                          sx={{
                            width: '100%',
                            maxWidth: '200px',
                            height: 'auto',
                            borderRadius: 1,
                            mb: 2,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        />
                        
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 1 }}>
                          Scan to share this business
                        </Typography>
                        
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ShareIcon />}
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = profileData.qrCodeUrl;
                            link.download = `${profileData.businessName}-QRCode.png`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          Download QR Code
                        </Button>
                      </Box>
                    </Paper>
                  )}
              </aside>
              </Grid>
            </Grid>
          )}
        </Container>      
      </main>
    </>
  );
};

export default BusinessDetails;